<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import ConversationApi from 'dashboard/api/inbox/conversation';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

// A Smith3D board card (kanban.smith3d.my) opened over the chat. Chatwoot's
// server fetches a one-time ticket (~60s, frame-only) that signs the agent in
// as themselves, so staff can move/edit/upload here exactly as in a tab.
const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  url: { type: String, required: true },
  title: { type: String, default: '' },
});

// close(updated): updated = the card page reported a save, refresh the panel.
const emit = defineEmits(['close']);

const KANBAN_ORIGIN = 'https://kanban.smith3d.my';

const { t } = useI18n();
const frameUrl = ref('');
const errorText = ref('');
const isLoading = ref(true);
let updated = false;

// Each load needs a fresh ticket — they're single use.
const loadTicket = async () => {
  isLoading.value = true;
  errorText.value = '';
  frameUrl.value = '';
  try {
    const { data } = await ConversationApi.getS3EmbedTicket(
      props.conversationId,
      props.url
    );
    frameUrl.value = data.url;
  } catch (error) {
    // 403 = this Chatwoot login isn't one of Smith3D's staff accounts.
    errorText.value =
      error?.response?.status === 403
        ? t('S3_CUSTOMER_PANEL.CARD.NOT_LINKED')
        : error?.response?.data?.error || t('S3_CUSTOMER_PANEL.CARD.FAILED');
  } finally {
    isLoading.value = false;
  }
};

const close = () => emit('close', updated);

const onMessage = event => {
  if (event.origin !== KANBAN_ORIGIN) return;
  let data = event.data;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return;
    }
  }
  if (data?.event === 's3CardUpdated') updated = true;
};

const onKeydown = event => {
  if (event.key === 'Escape') close();
};

onMounted(() => {
  window.addEventListener('message', onMessage);
  window.addEventListener('keydown', onKeydown);
  loadTicket();
});
onBeforeUnmount(() => {
  window.removeEventListener('message', onMessage);
  window.removeEventListener('keydown', onKeydown);
});
</script>

<template>
  <TeleportWithDirection>
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-modal-backdrop-dark"
      @click.self="close"
    >
      <div
        class="flex flex-col w-[min(1200px,94vw)] h-[88vh] overflow-hidden shadow-xl rounded-xl bg-n-solid-1"
      >
        <div
          class="flex items-center gap-2 px-4 py-2 border-b border-n-weak shrink-0"
        >
          <span class="flex-1 text-sm font-medium truncate text-n-slate-12">
            {{ title }}
          </span>
          <button
            v-tooltip.bottom="t('S3_CUSTOMER_PANEL.CARD.RELOAD')"
            type="button"
            class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-alpha-2"
            @click="loadTicket"
          >
            <Icon icon="i-lucide-refresh-cw" class="size-4" />
          </button>
          <a
            v-tooltip.bottom="t('S3_CUSTOMER_PANEL.CARD.NEW_TAB')"
            :href="url"
            target="_blank"
            rel="noopener noreferrer"
            class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-alpha-2"
          >
            <Icon icon="i-lucide-external-link" class="size-4" />
          </a>
          <button
            v-tooltip.bottom="t('S3_CUSTOMER_PANEL.CARD.CLOSE')"
            type="button"
            class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-alpha-2"
            @click="close"
          >
            <Icon icon="i-lucide-x" class="size-4" />
          </button>
        </div>
        <p v-if="isLoading" class="mx-auto text-sm text-n-slate-11 mt-[30vh]">
          {{ t('S3_CUSTOMER_PANEL.CARD.LOADING') }}
        </p>
        <div
          v-else-if="errorText"
          class="flex flex-col items-center gap-3 mx-auto mt-[30vh] max-w-md text-center"
        >
          <p class="mb-0 text-sm text-n-ruby-11">{{ errorText }}</p>
          <a
            :href="url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-sm text-n-blue-11 hover:underline"
            @click="close"
          >
            {{ t('S3_CUSTOMER_PANEL.CARD.NEW_TAB') }}
          </a>
        </div>
        <iframe
          v-else
          :src="frameUrl"
          :title="title"
          allow="clipboard-read; clipboard-write; fullscreen"
          class="flex-1 w-full border-0"
        />
      </div>
    </div>
  </TeleportWithDirection>
</template>
